import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { colors } from './styles';

//const DEFAULT_BASE = 'http://208.109.39.244:5000/api';

const DEFAULT_BASE = 'https://manna-of-life.com/api';
//const DEFAULT_BASE = 'http://192.168.0.107:5247/api';

// ── Shared styles ─────────────────────────────────────────────────────────────
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 700,
  color: colors.textMuted, marginBottom: 7,
  textTransform: 'uppercase', letterSpacing: '0.7px',
};

const inputStyle = (focused, name, focusedField) => ({
  width: '100%', padding: '12px 16px',
  border: `1px solid ${focusedField === name ? colors.borderFocus : colors.border}`,
  borderRadius: 10, background: colors.bgInput,
  color: colors.text, fontSize: 14, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s ease',
  boxShadow: focusedField === name ? '0 0 0 3px rgba(91,110,232,0.2)' : 'none',
});

const btnStyle = (loading) => ({
  width: '100%', padding: '12px 18px',
  background: loading ? colors.border : `linear-gradient(135deg, ${colors.primary}, #7080F0)`,
  color: colors.white, border: 'none', fontWeight: 700,
  borderRadius: 10, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit', letterSpacing: '-0.2px',
  boxShadow: loading ? 'none' : '0 4px 20px rgba(91,110,232,0.4)',
  transition: 'all 0.2s ease',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
});

function Spinner() {
  return (
    <>
      <span style={{
        display: 'inline-block', width: 14, height: 14,
        border: '2px solid rgba(255,255,255,0.3)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

function ErrorBox({ message }) {
  if (!message) return null;
  return (
    <div style={{
      background: 'rgba(248,113,113,0.12)', color: colors.danger,
      border: '1px solid rgba(248,113,113,0.35)',
      borderRadius: 10, padding: '10px 14px',
      fontSize: 13, marginBottom: '1.25rem',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span>⚠</span> {message}
    </div>
  );
}

function SuccessBox({ message }) {
  if (!message) return null;
  return (
    <div style={{
      background: 'rgba(52,211,153,0.12)', color: '#34D399',
      border: '1px solid rgba(52,211,153,0.35)',
      borderRadius: 10, padding: '10px 14px',
      fontSize: 13, marginBottom: '1.25rem',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span>✓</span> {message}
    </div>
  );
}

// ── Forgot Password Form ───────────────────────────────────────────────────────
function ForgotPasswordForm({ baseUrl, onBack }) {
  const [step, setStep]           = useState('request'); // 'request' | 'reset'
  const [email, setEmail]         = useState('');
  const [token, setToken]         = useState('');
  const [newPassword, setNew]     = useState('');
  const [confirmPw, setConfirm]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [focusedField, setFocus]  = useState(null);

  const fi = (name) => inputStyle(focusedField === name, name, focusedField);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send reset email.');
      setSuccess('Reset token sent! Check your email and enter the token below.');
      setStep('reset');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!token || !newPassword || !confirmPw) { setError('Please fill in all fields.'); return; }
    if (newPassword !== confirmPw) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${baseUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reset password.');
      setSuccess('Password reset successfully! You can now sign in.');
      setTimeout(() => onBack(), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: colors.primary,
          cursor: 'pointer', fontSize: 13, padding: 0,
          display: 'flex', alignItems: 'center', gap: 5, marginBottom: 12,
          fontFamily: 'inherit',
        }}>
          ← Back to Sign In
        </button>
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, letterSpacing: '-0.3px' }}>
          {step === 'request' ? 'Forgot Password' : 'Reset Password'}
        </div>
        <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
          {step === 'request'
            ? 'Enter your email and we\'ll send you a reset token.'
            : 'Enter the token from your email and set a new password.'}
        </div>
      </div>

      <ErrorBox message={error} />
      <SuccessBox message={success} />

      {step === 'request' && (
        <form onSubmit={handleRequest}>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={fi('email')}
              onFocus={() => setFocus('email')}
              onBlur={() => setFocus(null)}
              autoFocus
            />
          </div>
          <button type="submit" disabled={loading} className="login-btn" style={btnStyle(loading)}>
            {loading ? <><Spinner /> Sending…</> : 'Send Reset Token →'}
          </button>
        </form>
      )}

      {step === 'reset' && (
        <form onSubmit={handleReset}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Reset Token</label>
            <input
              type="text" value={token}
              onChange={e => setToken(e.target.value)}
              placeholder="Paste token from email"
              style={fi('token')}
              onFocus={() => setFocus('token')}
              onBlur={() => setFocus(null)}
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>New Password <span style={{ color: colors.textLight, fontWeight: 400, textTransform: 'none', fontSize: 11 }}>min 8 characters</span></label>
            <input
              type="password" value={newPassword}
              onChange={e => setNew(e.target.value)}
              placeholder="••••••••"
              style={fi('newpw')}
              onFocus={() => setFocus('newpw')}
              onBlur={() => setFocus(null)}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Confirm New Password</label>
            <input
              type="password" value={confirmPw}
              onChange={e => setConfirm(e.target.value)}
              placeholder="••••••••"
              style={fi('confirmpw')}
              onFocus={() => setFocus('confirmpw')}
              onBlur={() => setFocus(null)}
            />
          </div>
          <button type="submit" disabled={loading} className="login-btn" style={btnStyle(loading)}>
            {loading ? <><Spinner /> Resetting…</> : 'Reset Password →'}
          </button>
          <button type="button" onClick={() => setStep('request')} style={{
            width: '100%', marginTop: 10, padding: '10px',
            background: 'transparent', border: `1px solid ${colors.border}`,
            color: colors.textMuted, borderRadius: 10, cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit',
          }}>
            Resend Token
          </button>
        </form>
      )}
    </>
  );
}

// ── Change Password Form (used inside settings when logged in) ─────────────────
export function ChangePasswordForm({ baseUrl = DEFAULT_BASE, token: authToken }) {
  const [currentPw, setCurrent]   = useState('');
  const [newPw, setNew]           = useState('');
  const [confirmPw, setConfirm]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [focusedField, setFocus]  = useState(null);

  const fi = (name) => inputStyle(focusedField === name, name, focusedField);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) { setError('Please fill in all fields.'); return; }
    if (newPw !== confirmPw) { setError('New passwords do not match.'); return; }
    if (newPw.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (currentPw === newPw) { setError('New password must differ from current password.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${baseUrl}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw, confirmNewPassword: confirmPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to change password.');
      setSuccess('Password changed successfully!');
      setCurrent(''); setNew(''); setConfirm('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ErrorBox message={error} />
      <SuccessBox message={success} />
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Current Password</label>
          <input
            type="password" value={currentPw}
            onChange={e => setCurrent(e.target.value)}
            placeholder="••••••••"
            style={fi('current')}
            onFocus={() => setFocus('current')}
            onBlur={() => setFocus(null)}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>New Password <span style={{ color: colors.textLight, fontWeight: 400, textTransform: 'none', fontSize: 11 }}>min 8 characters</span></label>
          <input
            type="password" value={newPw}
            onChange={e => setNew(e.target.value)}
            placeholder="••••••••"
            style={fi('newpw')}
            onFocus={() => setFocus('newpw')}
            onBlur={() => setFocus(null)}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Confirm New Password</label>
          <input
            type="password" value={confirmPw}
            onChange={e => setConfirm(e.target.value)}
            placeholder="••••••••"
            style={fi('confirmpw')}
            onFocus={() => setFocus('confirmpw')}
            onBlur={() => setFocus(null)}
          />
        </div>
        <button type="submit" disabled={loading} className="login-btn" style={{ ...btnStyle(loading), width: 'auto', padding: '10px 24px' }}>
          {loading ? <><Spinner /> Updating…</> : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

// ── Main Login Page ────────────────────────────────────────────────────────────
export default function LoginPage({ baseUrl = DEFAULT_BASE }) {
  const { login } = useAuth();

  const [tab, setTab]           = useState('login'); // 'login' | 'register' | 'forgot'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [focusedField, setFocus] = useState(null);

  const fi = (name) => inputStyle(focusedField === name, name, focusedField);
  const clearError = () => setError('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${baseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Invalid email or password.');
      login(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!username || !email || !password) { setError('Please fill in all fields.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${baseUrl}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Registration failed.');
      login(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (t) => { setTab(t); clearError(); };

  return (
    <div style={{
      minHeight: '100vh', background: colors.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient glows */}
      <div style={{
        position: 'absolute', width: 500, height: 500, borderRadius: '50%',
        top: '-15%', left: '-10%',
        background: 'radial-gradient(circle, rgba(91,110,232,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        bottom: '-10%', right: '-5%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .login-card { animation: fadeSlideUp 0.4s ease forwards; }
        .login-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 28px rgba(91,110,232,0.5) !important;
          opacity: 0.95;
        }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
        input::placeholder { color: #3D4A6B; }
      `}</style>

      <div className="login-card" style={{
        background: colors.bgCard, border: `1px solid ${colors.border}`,
        borderRadius: 20, width: '100%', maxWidth: 420,
        padding: '2.5rem 2.25rem 2rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.4)',
        position: 'relative',
      }}>
        {/* Shimmer line */}
        <div style={{
          position: 'absolute', top: 0, left: '20%', right: '20%', height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(91,110,232,0.6), transparent)',
          borderRadius: 1,
        }} />

        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 64, height: 64, borderRadius: 16, marginBottom: 14,
            background: 'linear-gradient(135deg, rgba(91,110,232,0.25), rgba(139,92,246,0.2))',
            border: '1px solid rgba(91,110,232,0.35)', fontSize: 30,
            boxShadow: '0 4px 20px rgba(91,110,232,0.3)',
          }}>📖</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: colors.text, letterSpacing: '-0.6px', lineHeight: 1.2, marginBottom: 5 }}>
            Mannah of Life
          </div>
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700,
            color: colors.navActiveText, letterSpacing: '1.5px', textTransform: 'uppercase',
            background: 'rgba(91,110,232,0.15)', border: '1px solid rgba(91,110,232,0.25)',
            borderRadius: 20, padding: '3px 12px',
          }}>Admin Portal</div>
        </div>

        {/* Forgot password view */}
        {tab === 'forgot' && (
          <ForgotPasswordForm baseUrl={baseUrl} onBack={() => switchTab('login')} />
        )}

        {/* Login / Register views */}
        {tab !== 'forgot' && (
          <>
            <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: '1.5rem', letterSpacing: '-0.3px' }}>
              {tab === 'login' ? 'Sign In' : 'Create Account'}
            </div>

            <ErrorBox message={error} />

            {/* Login Form */}
            {tab === 'login' && (
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" style={fi('email')}
                    onFocus={() => setFocus('email')} onBlur={() => setFocus(null)} autoFocus />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={labelStyle}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" style={fi('password')}
                    onFocus={() => setFocus('password')} onBlur={() => setFocus(null)} />
                </div>

                {/* Forgot password link */}
                <div style={{ textAlign: 'right', marginBottom: 18 }}>
                  <button type="button" onClick={() => switchTab('forgot')} style={{
                    background: 'none', border: 'none', color: colors.primary,
                    cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: 'inherit',
                    textDecoration: 'underline', opacity: 0.8,
                  }}>
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading} className="login-btn" style={btnStyle(loading)}>
                  {loading ? <><Spinner /> Signing in…</> : 'Sign In →'}
                </button>
              </form>
            )}

            {/* Register Form */}
            {tab === 'register' && (
              <form onSubmit={handleRegister}>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Username</label>
                  <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                    placeholder="John Doe" style={fi('username')}
                    onFocus={() => setFocus('username')} onBlur={() => setFocus(null)} autoFocus />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" style={fi('email')}
                    onFocus={() => setFocus('email')} onBlur={() => setFocus(null)} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Password <span style={{ color: colors.textLight, fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 6, fontSize: 11 }}>min 6 characters</span></label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" style={fi('reg-password')}
                    onFocus={() => setFocus('reg-password')} onBlur={() => setFocus(null)} />
                </div>
                <button type="submit" disabled={loading} className="login-btn" style={btnStyle(loading)}>
                  {loading ? 'Creating account…' : 'Create Account →'}
                </button>
              </form>
            )}

            {/* Footer */}
            <div style={{
              textAlign: 'center', fontSize: 12, color: colors.textLight,
              marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: `1px solid ${colors.border}`,
            }}>
              🔒 Session saved locally · expires in 24 hours
            </div>
          </>
        )}
      </div>
    </div>
  );
}
